import { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid2,
  Divider,
  Tabs,
  Tab,
  Box,
  IconButton,
  Stack,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import lightTheme from '@/src/styles/themes/lightTheme';
import {
  DYNAMIC_INPUT_TYPES_ENUM,
  IAttributeOptions,
  INPUT_TYPES_ENUM,
  INPUT_FIELD_TYPE_MAP,
  UI_TYPE_INPUT_TYPE_MAP
} from '@/src/types/common.interface';
import { IAttributeDef } from '@/src/config/AttributesConfig';
import { mvelEditorConfiguration } from '@/src/config/mvel/MvelEditorSchemas';
import DynamicInput from '@/src/components/common/DynamicInput';

export type KeyValuePair = {
  key: string;
  value: string;
};

export type UserAttributeRow = {
  id: string;
  [key: string]: any;
};

export type SingleTestCaseForm = {
  id: string;
  test_case_name: string;
  api_config: string;
  params: KeyValuePair[];
  headers: KeyValuePair[];
  body: string;
  organization: string;
  user_name: string;
  user_type: string;
  user_attributes: UserAttributeRow[];
  role_names: string[];
};

export type TestCaseFormValues = {
  testCases: SingleTestCaseForm[];
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

type TestCaseAccordionProps = {
  control: any;
  errors: any;
  testCaseIndex: number;
  fieldId: string;
  handleCloneTestCase: () => void;
  onRemoveTestCase: () => void;
  organizationOptions: IAttributeOptions[];
  userTypeOptions: IAttributeOptions[];
  rolesOptions: IAttributeOptions[];
  apiConfigOptions: IAttributeOptions[];
  userAttributesDefinition: IAttributeDef[];
  clearErrors: (name: string) => void;
  onRunTest: () => void;
  isLoading: boolean;
};

export default function TestCaseAccordion(props: TestCaseAccordionProps) {
  const {
    control,
    errors,
    testCaseIndex,
    onRemoveTestCase,
    organizationOptions,
    handleCloneTestCase,
    userTypeOptions,
    rolesOptions,
    apiConfigOptions,
    userAttributesDefinition,
    clearErrors,
    onRunTest,
    isLoading
  } = props;

  const [isEditingTestCase, setIsEditingTestCase] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const testCaseInputRef = useRef<HTMLInputElement>(null);

  const {
    fields: paramFields,
    append: appendParam,
    remove: removeParam
  } = useFieldArray({
    control,
    name: `testCases.${testCaseIndex}.params`
  });

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader
  } = useFieldArray({
    control,
    name: `testCases.${testCaseIndex}.headers`
  });

  const {
    fields: userAttrFields,
    append: appendUserAttr,
    remove: removeUserAttr
  } = useFieldArray({
    control,
    name: `testCases.${testCaseIndex}.user_attributes`
  });

  const currentTestCase = (useWatch({
    control,
    name: `testCases.${testCaseIndex}`
  }) || {}) as SingleTestCaseForm;

  const testCaseName = currentTestCase?.test_case_name || '';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getAttributeDefByName = (name?: string) =>
    userAttributesDefinition.find((def) => def.name === name);

  const handleAddNewUserAttribute = () => {
    appendUserAttr({ id: uuidv4(), key: '', value: '' });
  };

  const renderKeyValueTab = (
    namePrefix: 'params' | 'headers' | 'user_attributes',
    fields: { id: string }[],
    idBase: number,
    onRemove: (index: number) => void,
    onAdd: () => void,
    addLabel: string
  ) => {
    return (
      <Stack spacing={2}>
        {fields.map((field, idx) => {
          const caseErrors = (errors?.testCases?.[testCaseIndex] as any) ?? {};
          const fieldErrors = caseErrors?.[namePrefix]?.[idx] ?? {};
          return (
            <Grid2 container spacing={2} key={field.id}>
              <Grid2 size={{ xs: 3 }}>
                <Controller
                  name={`testCases.${testCaseIndex}.${namePrefix}.${idx}.key`}
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DynamicInput
                      type={DYNAMIC_INPUT_TYPES_ENUM.TEXT}
                      id={idBase + idx * 2}
                      value={value}
                      hasError={!!fieldErrors?.key}
                      helperText={fieldErrors?.key?.message ?? ' '}
                      placeholder="Key"
                      onChange={(val: string) => onChange(val)}
                      onBlur={onBlur}
                      inputType={INPUT_TYPES_ENUM.TEXT}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 3 }}>
                <Controller
                  name={`testCases.${testCaseIndex}.${namePrefix}.${idx}.value`}
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DynamicInput
                      type={DYNAMIC_INPUT_TYPES_ENUM.TEXT}
                      id={idBase + 1 + idx * 2}
                      value={value}
                      hasError={!!fieldErrors?.value}
                      helperText={fieldErrors?.value?.message ?? ' '}
                      placeholder="Value"
                      onChange={(val: string) => onChange(val)}
                      onBlur={onBlur}
                      inputType={INPUT_TYPES_ENUM.TEXT}
                    />
                  )}
                />
              </Grid2>
              <IconButton
                color="error"
                onClick={() => onRemove(idx)}
                disabled={fields.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Grid2>
          );
        })}
        <Button
          startIcon={<AddIcon />}
          onClick={onAdd}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          {addLabel}
        </Button>
      </Stack>
    );
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${lightTheme.palette.grey[200]}`,
        borderRadius: 2,
        backgroundColor: lightTheme.palette.common.white,
        overflow: 'hidden'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          px: 2,
          py: 1.5,
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            maxHeight: 10
          },
          borderBottom: `1px solid ${lightTheme.palette.grey[200]}`
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {isEditingTestCase ? (
            <Controller
              name={`testCases.${testCaseIndex}.test_case_name`}
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Box
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.TEXT}
                    id={1 + testCaseIndex}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.test_case_name}
                    placeholder="Enter test case name"
                    onChange={(val: string) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.test_case_name`);
                    }}
                    onBlur={() => {
                      onBlur();
                      setIsEditingTestCase(false);
                    }}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    size="small"
                    maxLength={50}
                    sx={{ minWidth: 240 }}
                  />
                </Box>
              )}
            />
          ) : (
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingTestCase(true);
                setTimeout(() => testCaseInputRef.current?.focus(), 0);
              }}
              sx={{
                cursor: 'pointer',
                fontSize: '1rem',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                color: lightTheme.palette.grey[600],
                transition: 'background-color 0.2s'
              }}
            >
              {testCaseName || 'Click to add test case name'}
            </Typography>
          )}
        </Box>
        <Box paddingRight={1} display="flex" columnGap={1} flexShrink={0}>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onRunTest();
            }}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            size="small"
          >
            {isLoading ? 'Running...' : 'Run Test'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleCloneTestCase();
            }}
            size="small"
          >
            Clone
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveTestCase();
            }}
          >
            Remove
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box border={1} borderColor={lightTheme.palette.grey[300]} borderRadius={3} p={2}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 6 }}>
              <Controller
                name={`testCases.${testCaseIndex}.api_config`}
                control={control}
                rules={{ required: 'API config is required' }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                    id={8}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.api_config}
                    options={apiConfigOptions}
                    placeholder="Select API config"
                    onChange={(val: string) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.api_config`);
                    }}
                    onBlur={onBlur}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    label="Select API Config"
                    size="small"
                    helperText={
                      (errors?.testCases?.[testCaseIndex] as any)?.api_config?.message ?? ' '
                    }
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={(_event, newValue) => handleTabChange(_event, newValue)}
                >
                  {mvelEditorConfiguration.HEADER_TABS.map((tab) => (
                    <Tab key={tab.value as string} label={tab.label} />
                  ))}
                </Tabs>
              </Box>
              {mvelEditorConfiguration.HEADER_TABS.map((tab, index) => (
                <TabPanel key={tab.value as string} value={tabValue} index={index}>
                  {tab.value === 'params' &&
                    renderKeyValueTab(
                      'params',
                      paramFields,
                      200,
                      removeParam,
                      () => appendParam({ key: '', value: '' }),
                      'Add Param'
                    )}

                  {tab.value === 'headers' &&
                    renderKeyValueTab(
                      'headers',
                      headerFields,
                      100,
                      removeHeader,
                      () => appendHeader({ key: '', value: '' }),
                      'Add Header'
                    )}

                  {tab.value === 'body' && (
                    <Grid2 container>
                      <Grid2 size={{ xs: 6 }}>
                        <Controller
                          name={`testCases.${testCaseIndex}.body`}
                          control={control}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <DynamicInput
                              type={DYNAMIC_INPUT_TYPES_ENUM.LARGE_TEXT}
                              id={13}
                              value={value}
                              hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.body}
                              helperText={
                                (errors?.testCases?.[testCaseIndex] as any)?.body?.message ?? ' '
                              }
                              placeholder="Enter request body (JSON)"
                              onChange={(val: string) => onChange(val)}
                              onBlur={onBlur}
                              inputType={INPUT_TYPES_ENUM.TEXT}
                              maxRows={6}
                            />
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  )}
                </TabPanel>
              ))}
            </Grid2>
          </Grid2>
        </Box>
        <Box
          border={1}
          borderColor={lightTheme.palette.grey[300]}
          borderRadius={3}
          p={2}
          display="flex"
          flexDirection="column"
          rowGap={2}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color={lightTheme.palette.text.secondary}
          >
            Basic User Data
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 6 }}>
              <Controller
                name={`testCases.${testCaseIndex}.organization`}
                control={control}
                rules={{ required: 'Organization is required' }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                    id={3}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.organization}
                    options={organizationOptions}
                    placeholder="Select Organization"
                    onChange={(val: string) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.organization`);
                    }}
                    onBlur={onBlur}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    helperText={
                      (errors?.testCases?.[testCaseIndex] as any)?.organization?.message ?? ' '
                    }
                  />
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 6 }}>
              <Controller
                name={`testCases.${testCaseIndex}.user_name`}
                control={control}
                rules={{ required: 'User name is required' }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.TEXT}
                    id={4}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.user_name}
                    placeholder="Enter user name"
                    onChange={(val: string) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.user_name`);
                    }}
                    onBlur={onBlur}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    helperText={
                      (errors?.testCases?.[testCaseIndex] as any)?.user_name?.message ?? ' '
                    }
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 6 }}>
              <Controller
                name={`testCases.${testCaseIndex}.user_type`}
                control={control}
                rules={{ required: 'User type is required' }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                    id={5}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.user_type}
                    options={userTypeOptions}
                    placeholder="Select User Type"
                    onChange={(val: string) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.user_type`);
                    }}
                    onBlur={onBlur}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    helperText={
                      (errors?.testCases?.[testCaseIndex] as any)?.user_type?.message ?? ' '
                    }
                  />
                )}
              />
            </Grid2>

            <Grid2 size={{ xs: 6 }}>
              <Controller
                name={`testCases.${testCaseIndex}.role_names`}
                control={control}
                rules={{
                  validate: (val) =>
                    Array.isArray(val) && val.length > 0 ? true : 'At least one role is required'
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DynamicInput
                    type={DYNAMIC_INPUT_TYPES_ENUM.MULTI_SELECT}
                    id={7}
                    value={value}
                    hasError={!!(errors?.testCases?.[testCaseIndex] as any)?.role_names}
                    options={rolesOptions}
                    placeholder="Select roles"
                    onChange={(val: string[]) => {
                      onChange(val);
                      clearErrors(`testCases.${testCaseIndex}.role_names`);
                    }}
                    onBlur={onBlur}
                    inputType={INPUT_TYPES_ENUM.TEXT}
                    helperText={
                      (errors?.testCases?.[testCaseIndex] as any)?.role_names?.message ?? ' '
                    }
                  />
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={lightTheme.palette.text.secondary}
                >
                  User Attributes
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewUserAttribute}
                  sx={{ textTransform: 'none' }}
                  disabled={
                    userAttrFields.length >= userAttributesDefinition.length ||
                    !userAttributesDefinition.length
                  }
                >
                  Add row
                </Button>
              </Box>
              <Stack spacing={1.5}>
                {userAttrFields.map((field, attrIndex) => {
                  const selectedKeys = userAttrFields
                    .map((f, i) =>
                      i === attrIndex
                        ? null
                        : (control as any)._formValues?.testCases?.[testCaseIndex]
                            ?.user_attributes?.[i]?.key
                    )
                    .filter(Boolean);
                  return (
                    <Grid2 container spacing={1} alignItems="center" key={field.id}>
                      <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ mb: 0.5, display: 'block' }}
                        >
                          Attribute
                        </Typography>
                        <Controller
                          name={`testCases.${testCaseIndex}.user_attributes.${attrIndex}.key`}
                          control={control}
                          rules={{ required: 'Attribute is required' }}
                          render={({ field: { onChange, onBlur, value } }) => {
                            const options = userAttributesDefinition
                              .filter(
                                (def) => !selectedKeys.includes(def.name) || def.name === value
                              )
                              .map((def) => ({ label: def.ui_label, value: def.name }));
                            const fieldErrors =
                              (errors?.testCases?.[testCaseIndex] as any)?.user_attributes?.[
                                attrIndex
                              ]?.key ?? {};
                            return (
                              <DynamicInput
                                type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                                id={5000 + attrIndex}
                                value={value || ''}
                                hasError={!!fieldErrors?.message}
                                helperText={(fieldErrors?.message as string) ?? ' '}
                                options={options}
                                placeholder="Select attribute"
                                inputType={INPUT_TYPES_ENUM.TEXT}
                                onChange={(val: any) => {
                                  onChange(val);
                                  const def = getAttributeDefByName(val);
                                  const isMulti =
                                    def?.ui_type?.toLowerCase().includes('multi_select') ||
                                    def?.ui_type?.toLowerCase().includes('multi_select_chips');
                                  const defaultVal = isMulti ? [] : '';
                                  (control as any)._formValues.testCases[
                                    testCaseIndex
                                  ].user_attributes[attrIndex].value = defaultVal;
                                  clearErrors(
                                    `testCases.${testCaseIndex}.user_attributes.${attrIndex}.key`
                                  );
                                  clearErrors(
                                    `testCases.${testCaseIndex}.user_attributes.${attrIndex}.value`
                                  );
                                }}
                                onBlur={onBlur}
                                size="small"
                              />
                            );
                          }}
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ mb: 0.5, display: 'block' }}
                        >
                          Value
                        </Typography>
                        <Controller
                          name={`testCases.${testCaseIndex}.user_attributes.${attrIndex}.value`}
                          control={control}
                          rules={{
                            validate: (val, formValues) => {
                              const key = (formValues as any)?.testCases?.[testCaseIndex]
                                ?.user_attributes?.[attrIndex]?.key;
                              const def = getAttributeDefByName(key);
                              if (def?.is_mandatory) {
                                if (Array.isArray(val))
                                  return val.length > 0 || `${def.ui_label} is required`;
                                return (
                                  (val ?? '') !== '' || `${def?.ui_label ?? 'Value'} is required`
                                );
                              }
                              return true;
                            }
                          }}
                          render={({ field: { onChange, onBlur, value } }) => {
                            const key = (control as any)._formValues?.testCases?.[testCaseIndex]
                              ?.user_attributes?.[attrIndex]?.key;
                            const def = getAttributeDefByName(key);
                            const uiTypeKey = def?.ui_type ? def.ui_type.toUpperCase() : 'TEXT';
                            const valueTypeKey = def?.value_type
                              ? def.value_type.toUpperCase()
                              : 'TEXT';
                            const inputType =
                              UI_TYPE_INPUT_TYPE_MAP[uiTypeKey] ?? DYNAMIC_INPUT_TYPES_ENUM.TEXT;
                            const inputFieldType =
                              INPUT_FIELD_TYPE_MAP[valueTypeKey] ?? INPUT_TYPES_ENUM.TEXT;
                            const fieldErrors =
                              (errors?.testCases?.[testCaseIndex] as any)?.user_attributes?.[
                                attrIndex
                              ]?.value ?? {};
                            const hasError = !!fieldErrors?.message;
                            const helperText = (fieldErrors?.message as string) ?? ' ';
                            const normalizedValue =
                              value ??
                              (inputType === DYNAMIC_INPUT_TYPES_ENUM.MULTI_SELECT ||
                              inputType === DYNAMIC_INPUT_TYPES_ENUM.DYNAMIC_MULTI_CHIPS_SELECT
                                ? []
                                : '');

                            return (
                              <DynamicInput
                                type={inputType}
                                id={6000 + attrIndex}
                                value={normalizedValue}
                                hasError={hasError}
                                helperText={helperText}
                                options={def?.options ?? []}
                                placeholder={def?.placeholder ?? 'Enter value'}
                                inputType={inputFieldType}
                                onChange={(val: any) => {
                                  onChange(val);
                                  clearErrors(
                                    `testCases.${testCaseIndex}.user_attributes.${attrIndex}.value`
                                  );
                                }}
                                onBlur={onBlur}
                                size="small"
                              />
                            );
                          }}
                        />
                      </Grid2>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeUserAttr(attrIndex)}
                        sx={{ marginBottom: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid2>
                  );
                })}
              </Stack>
            </Grid2>
          </Grid2>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
